import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, BookOpen, Sparkles, ShieldCheck, MessageSquare, Loader2, Check,
} from 'lucide-react';
import { API_URL } from '../config';
import '../styles/FamilyProductsSection.css';

const API = `${API_URL}/api`;
const SHIELD = 'https://customer-assets.emergentagent.com/job_express-checkout-9/artifacts/gw8x19qe_Variant%201_Shield%20Only_Vector%20copy.png';

const FamilyProductsSection = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [err, setErr] = useState('');

  const notifyMe = async (e) => {
    e?.preventDefault?.();
    setErr('');
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setErr('Please enter a valid email.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/scamsmart/notify-me`, { email, source: 'home_family_tile' });
      setSubscribed(true);
    } catch {
      setErr("Couldn't save your spot — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="fps" data-testid="family-products-section">
      <div className="fps-bg" aria-hidden="true" />
      <div className="fps-container">
        <header className="fps-head">
          <span className="fps-eyebrow">For Australian families</span>
          <h2 className="fps-title">
            Two new ways to keep your family <span className="fps-italic">safe online.</span>
          </h2>
          <p className="fps-sub">
            Calm, practical, distilled from Australia's most trusted voices in child safety and
            cybersecurity. Built by All Secured. Made for parents who don't have time to wade
            through dozens of government PDFs.
          </p>
        </header>

        <div className="fps-grid">
          {/* ====================== CARD 1 — Parents' Handbook ====================== */}
          <article className="fps-card fps-card-handbook" data-testid="fps-card-handbook">
            {/* Full-bleed warm family photo + cinematic overlay */}
            <div className="fps-card-photo" aria-hidden="true" />
            <div className="fps-card-photo-overlay" aria-hidden="true" />
            <div className="fps-card-warm-glow" aria-hidden="true" />

            {/* Floating gold edition seal */}
            <div className="fps-edition-seal" aria-hidden="true">
              <svg viewBox="0 0 100 100" className="fps-edition-ring">
                <defs>
                  <path id="fps-seal-circle" d="M 50,50 m -36,0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0" />
                </defs>
                <text><textPath href="#fps-seal-circle" startOffset="0">ALL SECURED · EST 2026 · PARENTS' HANDBOOK ·</textPath></text>
              </svg>
              <div className="fps-edition-core">
                <span>Edition</span><strong>I</strong><span>2026</span>
              </div>
            </div>

            {/* Sources strip — top of card */}
            <div className="fps-sources-strip" data-testid="fps-sources">
              <span className="fps-sources-label">Distilled from</span>
              <span className="fps-sources-pills">
                <span>eSafety</span>
                <span>AFP</span>
                <span>ACCCE</span>
                <span>Kids Helpline</span>
                <span>+ 2 more</span>
              </span>
            </div>

            <div className="fps-card-body fps-card-body-handbook">
              <span className="fps-badge fps-badge-live">
                <span className="fps-badge-dot" /> Available now
              </span>
              <h3 className="fps-card-title fps-card-title-light">
                The Parents' Digital<br/>
                <span className="fps-card-title-italic-warm">Safety Handbook</span>
              </h3>
              <p className="fps-card-tagline fps-card-tagline-light">
                Australia's calmest, most practical guide to keeping your kids safe online.
              </p>
              <ul className="fps-card-bullets fps-card-bullets-light fps-card-bullets-warm">
                <li><Check size={14} /> 6 trusted Australian sources, one calm guide</li>
                <li><Check size={14} /> 36 pages, plain English, read in one evening</li>
                <li><Check size={14} /> Family Digital Agreement + warning signs by age</li>
              </ul>
              <div className="fps-card-foot">
                <div className="fps-card-price fps-card-price-light">
                  <span className="fps-price-currency">A$</span>
                  <span className="fps-price-num">9</span>
                  <span className="fps-price-cents">.99</span>
                </div>
                <Link to="/parents-handbook" className="fps-cta-gold" data-testid="fps-handbook-cta">
                  Get the Handbook <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </article>

          {/* ====================== CARD 2 — ScamSmart (Coming Soon) ====================== */}
          <article className="fps-card fps-card-scamsmart" data-testid="fps-card-scamsmart">
            {/* Cinematic background — drifting orbs + animated SMS preview */}
            <div className="fps-cinema-orb fps-cinema-orb-1" aria-hidden="true" />
            <div className="fps-cinema-orb fps-cinema-orb-2" aria-hidden="true" />
            <div className="fps-cinema-grain" aria-hidden="true" />

            {/* Animated SMS chat preview running on loop */}
            <div className="fps-chat" aria-hidden="true">
              <div className="fps-chat-bubble fps-chat-them fps-chat-anim-1">
                <span className="fps-chat-from">Unsaved number</span>
                <p>Hi Mum, dropped my phone — temp number 😩 Can you transfer $850 today?</p>
              </div>
              <div className="fps-chat-bubble fps-chat-me fps-chat-anim-2">
                <p>Who is this? I'll call your real number to verify.</p>
              </div>
              <div className="fps-chat-bubble fps-chat-shield fps-chat-anim-3">
                <ShieldCheck size={13} /> <span>Scam defended.</span>
              </div>
            </div>

            <div className="fps-card-body fps-card-body-dark">
              <span className="fps-badge fps-badge-soon">
                <Sparkles size={11} /> Coming soon
              </span>
              <h3 className="fps-card-title fps-card-title-light">
                ScamSmart<br/>
                <span className="fps-card-title-italic">family scam defence</span>
              </h3>
              <p className="fps-card-tagline fps-card-tagline-light">
                Train your family to recognise and defend against real Australian scams — with an AI
                that role-plays the scammer so you can practise safely.
              </p>
              <ul className="fps-card-bullets fps-card-bullets-light">
                <li><MessageSquare size={14} /> AI Practice Mode — chat against 6 scammer personas</li>
                <li><ShieldCheck size={14} /> Family Safe Word + Scam Radar</li>
                <li><BookOpen size={14} /> Premium 7-layer scenario library</li>
              </ul>

              {!subscribed ? (
                <form onSubmit={notifyMe} className="fps-notify-form" data-testid="fps-notify-form">
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                    className="fps-notify-input"
                    data-testid="fps-notify-input"
                  />
                  <button type="submit" disabled={submitting} className="fps-cta-outline" data-testid="fps-notify-submit">
                    {submitting ? <Loader2 size={14} className="fps-spin" /> : <>Notify me <ArrowRight size={14} /></>}
                  </button>
                </form>
              ) : (
                <div className="fps-notify-success" data-testid="fps-notify-success">
                  <ShieldCheck size={16} />
                  You're on the list. We'll email you when ScamSmart goes live.
                </div>
              )}
              {err && <p className="fps-notify-err">{err}</p>}
            </div>
          </article>
        </div>
      </div>
    </section>
  );
};

export default FamilyProductsSection;
